import { AppointmentType, EventType } from '../../constants'
import { uniq } from '../../util/fp'
import type { FeatureFlagName } from '../useFeatureFlags'
import type { TrialGateBlockedAppointmentType } from '.'

type TrialGateDefinition = {
  flag: FeatureFlagName
  reasonKey: string
  disabledOptionEvent: EventType
  substitutionAppliedEvent: EventType
  validationFallbackEvent: EventType
  // What the substitution offers instead when this type is blocked. Named per entry rather than
  // derived: the two follow-up types used to be each other's only alternative, but Hearing Aid
  // Evaluation has no such opposite.
  substituteWith: AppointmentType
}

/**
 * Which surface reported an event. The impression and fallback events are counted per surface (see
 * useTrialGateTracking), and one session can legitimately traverse all three, so without this the
 * three are indistinguishable in Mixpanel and the counts read as one inflated total (PX-3765).
 */
enum TrialGateSurface {
  APPOINTMENT_TYPE_SELECTION = 'appointmentTypeSelection',
  CONSULTANT_CONFIGURATION = 'consultantConfiguration',
  SCHEDULE_FORM = 'scheduleForm',
}

/**
 * Everything that differs between the blockable appointment types, in one place: the flag that
 * switches its blocking on, the copy shown when it blocks, its Mixpanel events, and what the
 * substitution offers instead. PX-1807 owns the FOLLOW_UP entry, PX-3311 the AFTERCARE_FOLLOW_UP
 * one, and PX-3749 the HEARING_AID_EVALUATION one.
 *
 * Keyed by blocked type, not by trial direction - one trial decision can block more than one type.
 * Being out of trial blocks FOLLOW_UP and HEARING_AID_EVALUATION together, which is why those two
 * share a flag: they are one rollout, not two. That matches the product model, which is a flag per
 * direction - what in-trial customers may book, and what out-of-trial customers may book - so the
 * coupled rollback is intended rather than incidental.
 *
 * A total Record rather than a Partial one, so a type added here cannot be left half-wired:
 * omitting any slot is a compile error instead of a silently missing reason or untracked event.
 *
 * The FOLLOW_UP event names are frozen: Mixpanel matches saved reports on the literal string, so
 * renaming them to something direction-neutral would cut existing reports off from new data. For
 * the same reason HEARING_AID_EVALUATION reuses FOLLOW_UP's fallback event rather than adding a
 * parallel one - the two always fail to reach a decision together, so a second event would
 * double-count the same session.
 */
const TRIAL_GATES: Record<TrialGateBlockedAppointmentType, TrialGateDefinition> = {
  [AppointmentType.FOLLOW_UP]: {
    flag: 'BLOCK_OUT_OF_TRIAL_FOLLOW_UP_SCHEDULING',
    reasonKey: 'pages.appointment.messages.outOfTrialFollowUpDisabledReason',
    disabledOptionEvent: EventType.OUT_OF_TRIAL_FOLLOW_UP_OPTION_DISABLED,
    substitutionAppliedEvent: EventType.OUT_OF_TRIAL_FOLLOW_UP_SUBSTITUTION_APPLIED,
    validationFallbackEvent: EventType.OUT_OF_TRIAL_FOLLOW_UP_VALIDATION_FALLBACK,
    substituteWith: AppointmentType.AFTERCARE_FOLLOW_UP,
  },
  [AppointmentType.HEARING_AID_EVALUATION]: {
    flag: 'BLOCK_OUT_OF_TRIAL_FOLLOW_UP_SCHEDULING',
    reasonKey: 'pages.appointment.messages.outOfTrialHearingAidEvaluationDisabledReason',
    disabledOptionEvent: EventType.OUT_OF_TRIAL_HEARING_AID_EVALUATION_OPTION_DISABLED,
    substitutionAppliedEvent: EventType.OUT_OF_TRIAL_HEARING_AID_EVALUATION_SUBSTITUTION_APPLIED,
    validationFallbackEvent: EventType.OUT_OF_TRIAL_FOLLOW_UP_VALIDATION_FALLBACK,
    substituteWith: AppointmentType.AFTERCARE_FOLLOW_UP,
  },
  [AppointmentType.AFTERCARE_FOLLOW_UP]: {
    flag: 'BLOCK_IN_TRIAL_AFTERCARE_FOLLOW_UP_SCHEDULING',
    reasonKey: 'pages.appointment.messages.inTrialAftercareFollowUpDisabledReason',
    disabledOptionEvent: EventType.IN_TRIAL_AFTERCARE_FOLLOW_UP_OPTION_DISABLED,
    substitutionAppliedEvent: EventType.IN_TRIAL_AFTERCARE_FOLLOW_UP_SUBSTITUTION_APPLIED,
    validationFallbackEvent: EventType.IN_TRIAL_AFTERCARE_FOLLOW_UP_VALIDATION_FALLBACK,
    substituteWith: AppointmentType.FOLLOW_UP,
  },
}

const TRIAL_GATE_TYPES = Object.keys(TRIAL_GATES) as TrialGateBlockedAppointmentType[]

// Deduplicated, because two types share one flag: useFeatureFlags returns its values positionally,
// so callers pair these names with those values by index and then look a type's flag up by name.
// Asking for the same flag twice would work, but it invites reading a *type's* index as its index
// in the result - an alignment that no longer holds now that types and flags are not one-to-one.
const TRIAL_GATE_FLAG_NAMES = uniq(TRIAL_GATE_TYPES.map((type) => TRIAL_GATES[type].flag)) as [FeatureFlagName, ...FeatureFlagName[]]

/**
 * The gate definition for an appointment type, or undefined for one the trial rule never blocks.
 *
 * The cast is the single place the partiality is handled: TRIAL_GATES is total over the types it
 * blocks, and every caller holds a plain AppointmentType, so without this each call site would
 * either cast for itself or index a Record with a key it cannot prove is present.
 */
const getTrialGate = (appointmentType: AppointmentType): TrialGateDefinition | undefined =>
  TRIAL_GATES[appointmentType as TrialGateBlockedAppointmentType]

/**
 * Shared empty set, so a surface that blocks nothing keeps one stable identity across renders for
 * the memos and effects that take the blocked set as a dependency.
 *
 * Typed as the narrow gate union rather than AppointmentType so it also satisfies useTrialGate's
 * own field; it widens to AppointmentType[] at every other use site.
 */
const NO_APPOINTMENT_TYPES: TrialGateBlockedAppointmentType[] = []

/** A type the trial rule refused, paired with what was offered in its place. */
type AppointmentTypeSubstitution = { from: AppointmentType; to: AppointmentType }

/** What to offer instead when the requested type is blocked, ignoring what the shop configures. */
const getSubstituteAppointmentType = (
  requestedType: AppointmentType,
  blockedAppointmentTypes: readonly AppointmentType[]
): AppointmentType | undefined =>
  blockedAppointmentTypes.includes(requestedType) ? getTrialGate(requestedType)?.substituteWith : undefined

/**
 * The replacement for a blocked type, but only when this shop's configuration actually offers it -
 * otherwise undefined, and the caller keeps the requested type.
 *
 * Substituting a type the shop does not offer is worse than leaving the blocked one in place: the
 * form lands on a value its own Select has no option for, so the field renders blank with no
 * duration and no resources, while any substitution note claims a type was chosen. Keeping the
 * blocked type at least renders it disabled with a coherent reason.
 *
 * Bundled with the lookup rather than left to each caller because several call sites need this
 * pairing, and a guard each of them has to remember is a guard one of them will forget.
 */
const getOfferedSubstituteAppointmentType = (
  requestedType: AppointmentType,
  blockedAppointmentTypes: readonly AppointmentType[],
  offeredAppointmentTypes: Partial<Record<AppointmentType, unknown>> | undefined
): AppointmentType | undefined => {
  const substituteType = getSubstituteAppointmentType(requestedType, blockedAppointmentTypes)

  return substituteType && offeredAppointmentTypes?.[substituteType] ? substituteType : undefined
}

export {
  getOfferedSubstituteAppointmentType,
  getSubstituteAppointmentType,
  getTrialGate,
  NO_APPOINTMENT_TYPES,
  TRIAL_GATES,
  TRIAL_GATE_FLAG_NAMES,
  TRIAL_GATE_TYPES,
  TrialGateSurface,
}
export type { AppointmentTypeSubstitution }
